import { AddCoin } from '@/components/icons/add-coin'
import { ArrowUpRight } from '@/components/icons/arrow-up-right'
import { Folder } from '@/components/icons/folder'
import { GrowCoin } from '@/components/icons/grow-coin'
import type { IconComponent } from '@/components/icons/icon'
import { Logo } from '@/components/icons/logo'
import { Telegram } from '@/components/icons/telegram'
import { X } from '@/components/icons/x'
import { NavLink } from '@/components/navbar/navlink'
import { cn } from '@/lib/utils'
import {
    type Route,
    type RoutePath,
    RoutePaths,
    type SocialMediaLinkKey,
    adminRoutes,
    infoRoutes,
    routes,
    socialMediaLinks,
    userRoutes,
} from '@/routes'
import { RolesMatch } from '@trinity/blockchain-management'
import { Roles } from '@trinity/blockchain-management'
import { type FC, Fragment } from 'react'
import { FaUserShield } from 'react-icons/fa'
import { GiToken } from 'react-icons/gi'
import { GrConfigure } from 'react-icons/gr'
import { MdAdminPanelSettings } from 'react-icons/md'
import { MdAttachMoney, MdBookmarkAdded, MdDashboard } from 'react-icons/md'
import { PiHandDeposit, PiHandWithdraw } from 'react-icons/pi'
import { RoleGuard } from '../role-guard'

const Icons: Partial<Record<RoutePath, IconComponent>> = {
    [RoutePaths.Portfolio]: Folder,
    [RoutePaths.UseUTY]: AddCoin,
    [RoutePaths.GetUTY]: GrowCoin,
    [RoutePaths.Documentation]: ArrowUpRight,
    [RoutePaths.MintUTY]: GiToken,
    [RoutePaths.AdminDashboard]: MdDashboard,
    [RoutePaths.AdminDeposits]: PiHandDeposit,
    [RoutePaths.AdminRedemptions]: PiHandWithdraw,
    [RoutePaths.AdminWhitelist]: MdBookmarkAdded,
    [RoutePaths.AdminRoles]: FaUserShield,
    [RoutePaths.AdminConfiguration]: GrConfigure,
    [RoutePaths.AdminFunds]: MdAttachMoney,
}

const LinkIcon: FC<{ route: Route }> = ({ route }) => {
    const Icon = Icons[route.path]
    return Icon ? <Icon /> : null
}

const SocialMediaIcons: Record<SocialMediaLinkKey, IconComponent> = {
    telegram: Telegram,
    x: X,
}

const SocialMediaIcon: FC<{ link: SocialMediaLinkKey }> = ({ link }) => {
    const Icon = SocialMediaIcons[link]
    return Icon ? <Icon /> : null
}

interface AdminNavbarContentProps {
    onNavLinkClick?: () => void
}

const AdminNavbarContent: FC<AdminNavbarContentProps> = ({ onNavLinkClick }) => {
    return (
        <Fragment>
            <NavLink route={routes[RoutePaths.AdminHome]} onClick={onNavLinkClick}>
                <MdAdminPanelSettings />
                {routes[RoutePaths.AdminHome].label}
            </NavLink>
            <ul className="flex flex-col gap-2 pl-6">
                {adminRoutes.map((route) => (
                    <li key={route.path} className="empty:hidden">
                        <NavLink key={route.path} route={route} onClick={onNavLinkClick}>
                            <LinkIcon route={route} />
                            {route.label}
                        </NavLink>
                    </li>
                ))}
            </ul>
        </Fragment>
    )
}

interface Props {
    onNavLinkClick?: () => void
    className?: string
}

export const NavbarContent: FC<Props> = ({ onNavLinkClick, className }) => {
    return (
        <nav
            className={cn(
                'flex flex-col justify-between py-5 px-4 border rounded-[12px] h-full overflow-auto',
                className,
            )}
        >
            <div className="flex flex-col gap-6">
                <Logo />
                <div className="flex flex-col gap-2">
                    {userRoutes.map((route) => (
                        <NavLink key={route.path} route={route} onClick={onNavLinkClick}>
                            <LinkIcon route={route} />
                            {route.label}
                        </NavLink>
                    ))}
                    <RoleGuard roles={[Roles.Admin, Roles.Operator, Roles.DefaultAdmin]} match={RolesMatch.OneOf}>
                        <AdminNavbarContent onNavLinkClick={onNavLinkClick} />
                    </RoleGuard>
                </div>
            </div>
            <div className="flex flex-col gap-1 border-t border-separator pt-3">
                {infoRoutes.map((route) => (
                    <NavLink key={route.path} route={route} className="justify-between" onClick={onNavLinkClick}>
                        {route.label}
                        <LinkIcon route={route} />
                    </NavLink>
                ))}

                <div className="flex flex-row gap-6">
                    {socialMediaLinks.map((link) => (
                        <a
                            key={link.key}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-[0.5625rem]"
                        >
                            <SocialMediaIcon link={link.key} />
                        </a>
                    ))}
                </div>
            </div>
        </nav>
    )
}
